import {Component, ElementRef, HostBinding, Inject, Input, OnDestroy, OnInit, Renderer2, ViewChild} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import * as L from 'leaflet';
import {Map} from 'leaflet';
import {ProjectBusinessLogicSyncFilterService} from '../../project-business-logic-filter/project-business-logic-sync-filter.service';
import {BehaviorSubject, combineLatest, Subject, Subscription} from 'rxjs';
import {distinctUntilChanged, startWith, switchMap} from 'rxjs/operators';
import {RegionModel} from '../../models/region.model';
import {ProjectBusinessLogicFilterModel} from '../../project-business-logic-filter/project-business-logic-filter.model';
import {LeafletRegionsControlService} from './leaflet-regions-control.service';

@Component({
  selector: 'app-leaflet-regions-control',
  templateUrl: './leaflet-regions-control.component.html',
  styleUrls: ['leaflet-regions-control.component.scss']
})
export class LeafletRegionsControlComponent implements OnInit, OnDestroy {
  @HostBinding('class.dashboard-controls__container_right-absolute') dashboardControlsContainerAbsolute = true;
  @Input() map: Map;
  @ViewChild('regionToggleContainer') regionToggleContainer: ElementRef;
  @ViewChild('searchInput') searchInput: ElementRef;
  subscriptions: Subscription[] = [];
  filteredRegions: BehaviorSubject<RegionModel[]> = new BehaviorSubject([]);
  allRegions: RegionModel[] = [];
  regionVisibility = false;
  searchSubject = new Subject<string>();
  isLoading = new BehaviorSubject<boolean>(false);

  constructor(
    private http: HttpClient,
    @Inject('ENVIRONMENT_API_BASEPOINT') protected environmentApiBasepoint,
    private renderer: Renderer2,
    protected projectBusinessLogicSyncFilterService: ProjectBusinessLogicSyncFilterService,
    private selectedRegionService: LeafletRegionsControlService,
  ) {
    this.renderer.listen('window', 'click', (e: Event) => {
      if (this.regionToggleContainer !== undefined) {
        if (!this.regionToggleContainer.nativeElement.contains(e.target)) {
          this.regionVisibility = false;
          this.resetSearch();
        }
      }
    });
  }

  selectUserRegion(region: RegionModel) {
    this.regionVisibility = false;
    this.resetSearch();

    this.selectedRegionService.setSelectedRegion(region);

    this.map.fitBounds(L.geoJSON(JSON.parse(region.boundary)).getBounds());
  }

  openControlContainer() {
    const regions = this.allRegions;

    if (regions.length > 1) {
      this.regionVisibility = !this.regionVisibility;
    }
    if (regions.length === 1) {
      this.map.fitBounds(L.geoJSON(JSON.parse(regions[0].boundary)).getBounds());
    }
  }

  getRegions(params: HttpParams) {
    return this.http.get<RegionModel[]>(
      this.environmentApiBasepoint + 'region',
      {observe: 'body', params: params}
    );
  }

  buildParams(projectBusinessLogicFilter: ProjectBusinessLogicFilterModel, search: string): HttpParams {
    let params = new HttpParams()
      .append('per-page', '1000');

    if (projectBusinessLogicFilter) {
      if (projectBusinessLogicFilter.project_id !== null) {
        params = params.append('RegionSearch[project_id]', projectBusinessLogicFilter.project_id.toString());
      }
      if (projectBusinessLogicFilter.business_logic_id !== null) {
        params = params.append('RegionSearch[business_logic_id]', projectBusinessLogicFilter.business_logic_id.toString());
      }
    }

    if (search) {
      params = params.append('RegionSearch[name]', search);
    }

    return params;
  }

  ngOnInit() {
    this.subscriptions.push(
      combineLatest([
        this.projectBusinessLogicSyncFilterService.getFormattedProjectBusinessLogicFilter(),
        this.searchSubject.pipe(startWith(''), distinctUntilChanged())
      ])
        .pipe(
          switchMap(([filters, search]) => {
            this.isLoading.next(true);
            const params = this.buildParams(filters, search);
            return this.getRegions(params);
          })
        ).subscribe(regions => {
        const sorted = regions.sort((a, b) =>
          a.name.toLowerCase().localeCompare(b.name.toLowerCase())
        );

        if (this.allRegions.length === 0) {
          this.allRegions = sorted;
        }

        this.filteredRegions.next(sorted);
        this.isLoading.next(false);
      })
    );
  }

  onValueSearchChange(event: Event) {
    const target = event.target as HTMLInputElement;
    this.searchSubject.next(target.value);
  }

  private resetSearch() {
    if (this.searchInput && this.searchInput.nativeElement) {
      this.searchInput.nativeElement.value = '';
    }
    this.searchSubject.next('');
  }

  ngOnDestroy() {
    this.selectedRegionService.clearSelectedRegion();
    this.subscriptions.filter(s => !!s).forEach(s => s.unsubscribe());
  }
}
